import React, { createContext, useReducer, useContext } from "react";
import { JWTContextType, ActionMap, AuthState, AuthUser } from "../types/auth";
import { isSessionExpired, setSession } from "../utils/auth";
import http from "../utils/http";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAppDispatch from "../hooks/useAppDispatch";
import { setUser, unsetUser } from "../redux/slices/user/user";
import { getActiveNews } from "../api/news";
import { News } from "../types/be/news";
import { setNews } from "../redux/slices/news";
import { route } from "../utils/url";
import { ThemeContext } from "./ThemeContext";
import { BE_THEMES, THEMES } from "../constants";

export const SIGN_IN = "SIGN_IN";
export const SIGN_OUT = "SIGN_OUT";

export type AuthActionTypes = {
  [SIGN_IN]: {
    user: AuthUser;
  };
  [SIGN_OUT]: undefined;
};

export const getInitialState = (): AuthState => {
  return {
    isAuthenticated: isSessionExpired(),
    isInitialized: false,
    user: null,
  };
};

export const JWTReducer = (
  state: AuthState,
  action: ActionMap<AuthActionTypes>[keyof ActionMap<AuthActionTypes>]
): AuthState => {
  switch (action.type) {
    case SIGN_IN:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    case SIGN_OUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

type Props = {
  children: React.ReactElement;
};
function AuthProvider({ children }: Props): React.ReactElement {
  const [state, dispatch] = useReducer(JWTReducer, getInitialState());
  const dispatchStore = useAppDispatch();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { state: locationState }: any = useLocation();

  const { setTheme } = useContext(ThemeContext);

  const signIn = async (email: string, password: string): Promise<any> => {
    const response = await http.post(`/api/login`, {
      email,
      password,
    });
    if (response.status === 200) {
      const { access_token, token_expiration_date, data } =
        await response.json();
      setSession(access_token, token_expiration_date);
      /* istanbul ignore next */
      if (data.is_new_password) {
        dispatchStore(setUser(data));
        return navigate(route("reset-password"));
      }
      dispatch({
        type: SIGN_IN,
        payload: {
          user: data,
        },
      });
      i18n.changeLanguage(data.locale);
      window.localStorage.setItem("lang", data.locale);
      /* istanbul ignore next */
      setTheme(data.theme === BE_THEMES.LIGHT ? THEMES.DEFAULT : THEMES.DARK);
      localStorage.setItem(
        "dashboard",
        JSON.stringify({ layouts: data?.configuration?.dashboard?.layouts })
      );
      dispatchStore(setUser(data));
      getActiveNews()
        .then((res) => res.json())
        .then(({ data }: { data: News }) => dispatchStore(setNews(data)));
      return navigate(locationState?.from || route("root"));
    } else {
      const { message } = await response.json();
      throw new Error(JSON.stringify({ message, status: response?.status }));
    }
  };
  const signOut = async (): Promise<void> => {
    setSession(null, 0);
    dispatch({ type: SIGN_OUT });
    dispatchStore(unsetUser());
  };
  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
